import { FilterConfig, RowConfig, ColumnConfig } from '@/shared/types/table';
import { getRelativeDateRange, isDateString, parseDate } from './tableFilterDateHelpers';

export const filterRows = <T>(
  rows: RowConfig<T>[],
  filters: FilterConfig[],
  columns: ColumnConfig<T>[]
): RowConfig<T>[] => {
  if (filters.length === 0) return rows;

  return rows.filter((row) => {
    return filters.every((filter) => {
      const column = columns.find((col) => col.id === filter.columnId);
      if (!column) return true;

      const value = column.accessor
        ? typeof column.accessor === 'function'
          ? column.accessor(row)
          : row[column.accessor]
        : null;

      return matchesFilter(value, filter);
    });
  });
};

const matchesFilter = (value: unknown, filter: FilterConfig): boolean => {
  const filterValue = filter.value;
  const isValueEmpty = value === null || value === undefined || value === '';

  // Operadores que no requieren valor
  switch (filter.operator) {
    case 'isEmpty':
      return isValueEmpty;
    case 'isNotEmpty':
      return !isValueEmpty;
  }

  if (isValueEmpty) return false;

  const stringValue = String(value).toLowerCase();
  const stringFilterValue = String(filterValue).toLowerCase();

  switch (filter.operator) {
    case 'equals':
      if (isDateString(value) && isDateString(filterValue)) {
        return parseDate(value as string).getTime() === parseDate(filterValue as string).getTime();
      }
      return stringValue === stringFilterValue;

    case 'notEquals':
      if (isDateString(value) && isDateString(filterValue)) {
        return parseDate(value as string).getTime() !== parseDate(filterValue as string).getTime();
      }
      return stringValue !== stringFilterValue;

    case 'contains':
      return stringValue.includes(stringFilterValue);

    case 'notContains':
      return !stringValue.includes(stringFilterValue);

    case 'startsWith':
      return stringValue.startsWith(stringFilterValue);

    case 'endsWith':
      return stringValue.endsWith(stringFilterValue);

    case 'gt':
      if (isDateString(value) && isDateString(filterValue)) {
        return parseDate(value as string).getTime() > parseDate(filterValue as string).getTime();
      }
      return Number(value) > Number(filterValue);

    case 'gte':
      if (isDateString(value) && isDateString(filterValue)) {
        return parseDate(value as string).getTime() >= parseDate(filterValue as string).getTime();
      }
      return Number(value) >= Number(filterValue);

    case 'lt':
      if (isDateString(value) && isDateString(filterValue)) {
        return parseDate(value as string).getTime() < parseDate(filterValue as string).getTime();
      }
      return Number(value) < Number(filterValue);

    case 'lte':
      if (isDateString(value) && isDateString(filterValue)) {
        return parseDate(value as string).getTime() <= parseDate(filterValue as string).getTime();
      }
      return Number(value) <= Number(filterValue);

    case 'between':
      if (!filter.value2) return false;
      if (isDateString(value) && isDateString(filterValue) && isDateString(filter.value2)) {
        const valueTime = parseDate(value as string).getTime();
        const minTime = Math.min(
          parseDate(filterValue as string).getTime(),
          parseDate(filter.value2 as string).getTime()
        );
        const maxTime = Math.max(
          parseDate(filterValue as string).getTime(),
          parseDate(filter.value2 as string).getTime()
        );
        return valueTime >= minTime && valueTime <= maxTime;
      }
      const numValue = Number(value);
      const min = Math.min(Number(filterValue), Number(filter.value2));
      const max = Math.max(Number(filterValue), Number(filter.value2));
      return numValue >= min && numValue <= max;

    case 'in':
      if (!Array.isArray(filterValue)) return false;
      if (Array.isArray(value)) {
        return value.some((v) =>
          filterValue.some((fv) => String(fv).toLowerCase() === String(v).toLowerCase())
        );
      }
      return filterValue.some((fv) => String(fv).toLowerCase() === stringValue);

    // Operadores específicos de fecha
    case 'afterDate':
      if (!isDateString(value) || !isDateString(filterValue)) return false;
      return parseDate(value as string).getTime() > parseDate(filterValue as string).getTime();

    case 'beforeDate':
      if (!isDateString(value) || !isDateString(filterValue)) return false;
      return parseDate(value as string).getTime() < parseDate(filterValue as string).getTime();

    // Operadores de fechas relativas
    case 'thisMonth':
    case 'lastMonth':
    case 'thisYear':
    case 'lastYear': {
      if (!isDateString(value)) return false;
      const valueDate = parseDate(value as string);
      const { start, end } = getRelativeDateRange(filter.operator);
      const valueTime = valueDate.getTime();
      return valueTime >= start.getTime() && valueTime <= end.getTime();
    }

    default:
      return true;
  }
};

export const searchRows = <T>(
  rows: RowConfig<T>[],
  searchQuery: string,
  columns: ColumnConfig<T>[]
): RowConfig<T>[] => {
  if (!searchQuery.trim()) return rows;

  const query = searchQuery.toLowerCase();

  return rows.filter((row) => {
    return columns.some((column) => {
      const value = column.accessor
        ? typeof column.accessor === 'function'
          ? column.accessor(row)
          : row[column.accessor]
        : null;

      if (value === null || value === undefined) return false;

      return String(value).toLowerCase().includes(query);
    });
  });
};
